import { useAuthStore } from "@/stores/index.js";
import { useRoute } from "vue-router";

export { getInitials } from "@/lib/avatar";


export const isImage = (type) => {
  const imageTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'png',
    'jpg',
    'jpeg',
    'gif',
    'webp',
    'svg'
  ]
  return imageTypes.includes(type)
}


export const formatDatetime = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** MM/DD/YYYY — the app-wide date display format. */
export const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

export const formatTime = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Relative time for notifications (e.g. "2 mins ago") */
export const formatRelativeTime = (date) => {
  const d = new Date(date)
  const now = new Date()
  const diffMs = now - d
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)
  if (diffSec < 60) return 'Just now'
  if (diffMin < 60) return `${diffMin} min${diffMin !== 1 ? 's' : ''} ago`
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`
  return formatDate(d)
}

export const formatPrice = (value, currency = "USD") => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });

  return formatter.format(value);
};

export const formatFileSize = (bytes) => {
  if (!bytes) return
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`
}

export const isMac = () => {
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0
}

export const getFilePreview = (file) => {
  if (!file) return ''
  if (typeof file === 'string') {
    return file
  } else {
    return URL.createObjectURL(file)
  }
}

const resolveDownloadFileName = (fileUrl, fileName = null) => {
  if (fileName) return fileName
  try {
    return decodeURIComponent(String(fileUrl).split('/').pop().split('?')[0])
  } catch {
    return 'download'
  }
}

const isSameOriginUrl = (fileUrl) => {
  try {
    return new URL(fileUrl, window.location.href).origin === window.location.origin
  } catch {
    return false
  }
}

const triggerAnchorDownload = (href, fileName) => {
  const link = document.createElement('a')
  link.href = href
  if (fileName) link.download = fileName
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Downloads a file. Same-origin uses blob fetch (reliable filename).
 * Cross-origin uses a direct link — fetch would fail CORS on storage hosts.
 */
export const downloadFile = async (fileUrl, fileName = null) => {
  if (!fileUrl) throw new Error('No file URL provided')

  const resolvedFileName = resolveDownloadFileName(fileUrl, fileName)

  if (!isSameOriginUrl(fileUrl)) {
    triggerAnchorDownload(fileUrl, resolvedFileName)
    return 'success'
  }

  const response = await fetch(fileUrl)
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

  const blob = await response.blob()
  const blobUrl = URL.createObjectURL(blob)
  triggerAnchorDownload(blobUrl, resolvedFileName)
  URL.revokeObjectURL(blobUrl)
  return 'success'
}


export const hasAnyPermission = (list = []) => {
  const authStore = useAuthStore()
  if (authStore.isSuperAdmin) return true
  if (!list.length) return true
  if (list[0] === 'allowAuthenticated') return true

  if (typeof list === 'string') {
    list = [list]
  }


  return list?.some(p => authStore.allPermissions.includes(p))
}

export const hasAllPermissions = (list = []) => {
  const authStore = useAuthStore()
  if (authStore.isSuperAdmin) return true
  if (!list.length) return true

  return list.every(p => authStore.allPermissions.includes(p))
}

export const getTitleFromKey = (key) => {
  return key.replace(/_/g, ' ')
}

export const insideWorkspace = () => {
  const route = useRoute()
  return route.params.workspace_id
}
